import React, {useState} from "react";
import {
    Alert,
    Box,
    Button,
    Dialog,
    DialogContent,
    IconButton,
    Stack,
    Typography
} from "@mui/material";
import {Bell, Building, CalendarDays, Download, FileText, Receipt, RefreshCw, X} from "lucide-react";
import ComposicaoDaFatura from "../../../shared/ui/ComposicaoDaFatura";
import {getBackendErrorMessage} from "../../../shared/utils/httpError";
import {formatCompetencia, formatDateBR, formatMoney, getDiasAtraso, getFaturaStatusLabel, getFaturaStatusPalette} from "../financeiroUtils";
import {composicaoDaFatura, getMeioDaCobrancaLabel} from "../cobrancasUtils";
import {getPdfDaFatura} from "../FinanceiroService";
import CobrancasDaFatura from "./CobrancasDaFatura";
import FaturaReprocessDialog from "./FaturaReprocessDialog";

export default function FaturaDetailsDialog({
    open,
    details,
    loading,
    onClose,
    onNotify,
    onReprocess,
    onChanged,
    saving = false,
}) {
    const [reprocessOpen, setReprocessOpen] = useState(false);
    const [pdf, setPdf] = useState({carregando: false, erro: ''});
    const fatura = details?.fatura;
    const itens = details?.itens || [];
    const temSecoes = Boolean(details?.secoes?.length);

    // O link do PDF vale poucos minutos: pede na hora do clique e abre numa aba nova (BBF-141).
    const abrirPdf = async () => {
        if (!fatura?.id) return;
        setPdf({carregando: true, erro: ''});
        try {
            const response = await getPdfDaFatura(fatura.id);
            const url = response?.data?.data?.url;
            if (!url) throw new Error('sem link');
            window.open(url, '_blank', 'noopener');
            setPdf({carregando: false, erro: ''});
        } catch (error) {
            setPdf({carregando: false, erro: getBackendErrorMessage(error, 'O PDF da fatura não está disponível agora. Tente de novo em instantes.')});
        }
    };

    if (!open) return null;

    const palette = getFaturaStatusPalette(fatura?.status);
    const diasAtraso = getDiasAtraso(fatura?.vencimento);
    const canReprocess = fatura?.status && !['PAGA', 'CANCELADA'].includes(fatura.status);

    return (
        <>
            <Dialog
                open={open}
                onClose={onClose}
                fullWidth
                maxWidth="lg"
                sx={{'& .MuiDialog-paper': {borderRadius: 2, maxHeight: '90vh'}}}
            >
                <Box sx={{display: 'flex', alignItems: 'center', justifyContent: 'space-between', p: 3, borderBottom: '1px solid #e5e7eb', position: 'sticky', top: 0, bgcolor: '#fff', zIndex: 2}}>
                    <Box>
                        <Typography variant="h5" sx={{color: '#111827', fontWeight: 600}}>
                            Detalhes da Fatura
                        </Typography>
                        <Typography variant="body2" sx={{color: '#6b7280', mt: 0.5}}>
                            {fatura ? `Fatura ${fatura.numero || fatura.id}${fatura.referencia ? ` · ${fatura.referencia}` : ''}` : 'Carregando...'}
                        </Typography>
                    </Box>
                    <IconButton onClick={onClose}>
                        <X size={20} color="#6b7280" />
                    </IconButton>
                </Box>

                <DialogContent sx={{p: 3}}>
                    {!fatura ? (
                        <Typography color="text.secondary">Carregando detalhes da fatura...</Typography>
                    ) : (
                        <Stack spacing={3}>
                            <Box sx={{p: 2, borderRadius: 2, border: `2px solid ${palette.border}`, bgcolor: palette.bg}}>
                                <Stack direction={{xs: 'column', md: 'row'}} alignItems={{md: 'center'}} justifyContent="space-between" spacing={2}>
                                    <Box>
                                        <Typography sx={{fontSize: '1.125rem', fontWeight: 600, color: palette.color}}>
                                            Situação: {getFaturaStatusLabel(fatura.status)}
                                        </Typography>
                                        <Typography variant="body2" sx={{color: palette.color, opacity: 0.9}}>
                                            {fatura.status === 'PAGA' ? `Pago em ${formatDateBR(fatura.data_pagamento || fatura.atualizado_em)}` : null}
                                            {fatura.status === 'ABERTA' ? 'Aguardando pagamento' : null}
                                            {fatura.status === 'PAGA_EM_PARTE' ? 'Parte das cobranças já foi paga; o resto continua em aberto.' : null}
                                            {fatura.status === 'VENCIDA' ? `Vencida há ${diasAtraso} dias` : null}
                                            {fatura.status === 'EM_ANALISE' ? 'Processamento em análise' : null}
                                            {fatura.status === 'CANCELADA' ? 'Fatura cancelada' : null}
                                        </Typography>
                                    </Box>
                                </Stack>
                            </Box>

                            <Box sx={{display: 'grid', gridTemplateColumns: {xs: '1fr', md: '1fr 1fr'}, gap: 2}}>
                                <Box sx={{bgcolor: '#f9fafb', borderRadius: 2, p: 2}}>
                                    <Stack direction="row" spacing={1} alignItems="center" sx={{mb: 1.5, color: '#374151'}}>
                                        <Building size={18} />
                                        <Typography sx={{fontWeight: 500}}>Cliente</Typography>
                                    </Stack>
                                    <Stack spacing={1}>
                                        <Box>
                                            <Typography variant="body2" sx={{color: '#6b7280'}}>Empresa</Typography>
                                            <Typography sx={{color: '#111827'}}>{fatura.razao_social || fatura.empresa_nome || fatura.empresa?.nome || '-'}</Typography>
                                        </Box>
                                        <Box>
                                            <Typography variant="body2" sx={{color: '#6b7280'}}>Nome fantasia</Typography>
                                            <Typography sx={{color: '#111827'}}>{fatura.nome_fantasia || '-'}</Typography>
                                        </Box>
                                        <Box>
                                            <Typography variant="body2" sx={{color: '#6b7280'}}>CNPJ</Typography>
                                            <Typography sx={{color: '#111827'}}>{fatura.cnpj || '-'}</Typography>
                                        </Box>
                                    </Stack>
                                </Box>

                                <Box sx={{bgcolor: '#f9fafb', borderRadius: 2, p: 2}}>
                                    <Stack direction="row" spacing={1} alignItems="center" sx={{mb: 1.5, color: '#374151'}}>
                                        <CalendarDays size={18} />
                                        <Typography sx={{fontWeight: 500}}>Informações</Typography>
                                    </Stack>
                                    <Stack spacing={1}>
                                        <Box>
                                            <Typography variant="body2" sx={{color: '#6b7280'}}>Competência</Typography>
                                            <Typography sx={{color: '#111827'}}>{formatCompetencia(fatura.competencia)}</Typography>
                                        </Box>
                                        <Box>
                                            <Typography variant="body2" sx={{color: '#6b7280'}}>Data de Emissão</Typography>
                                            <Typography sx={{color: '#111827'}}>{formatDateBR(fatura.emitida_em || fatura.criado_em)}</Typography>
                                        </Box>
                                        <Box>
                                            <Typography variant="body2" sx={{color: '#6b7280'}}>Vencimento</Typography>
                                            <Typography sx={{color: fatura.status === 'VENCIDA' ? '#dc2626' : '#111827'}}>
                                                {formatDateBR(fatura.vencimento)}
                                            </Typography>
                                        </Box>
                                        {fatura.forma_pagamento && !temSecoes ? (
                                            <Box>
                                                <Typography variant="body2" sx={{color: '#6b7280'}}>Forma de Pagamento</Typography>
                                                <Typography sx={{color: '#111827'}}>{fatura.forma_pagamento}</Typography>
                                            </Box>
                                        ) : null}
                                    </Stack>
                                </Box>
                            </Box>

                            {fatura.status === 'VENCIDA' ? (
                                <Alert severity="error">
                                    A régua conta do vencimento: em atraso no dia seguinte, somente consulta com 15 dias, suspensa com 30 e
                                    cancelada com 60. Pago depois do vencimento, o boleto cobra multa de 2% e juros de 1% ao mês.
                                </Alert>
                            ) : null}

                            {temSecoes ? (
                                <Box sx={{bgcolor: '#f9fafb', borderRadius: 2, p: 2}}>
                                    <Stack direction="row" spacing={1} alignItems="center" sx={{mb: 2, color: '#374151'}}>
                                        <FileText size={18} />
                                        <Typography sx={{fontWeight: 500}}>Assinaturas</Typography>
                                    </Stack>
                                    <ComposicaoDaFatura
                                        previa={composicaoDaFatura(details)}
                                        rotuloDaSecao={(secao) => `${secao.plano}${secao.versao ? ` v${secao.versao}` : ''}`}
                                        detalheDaSecao={(secao) => (
                                            <Typography variant="caption" color="text.secondary">
                                                {[secao.chave, getMeioDaCobrancaLabel(secao.meio)].filter(Boolean).join(' · ')}
                                            </Typography>
                                        )}
                                    />
                                </Box>
                            ) : null}

                            {!temSecoes ? (
                            <Box sx={{bgcolor: '#f9fafb', borderRadius: 2, p: 2}}>
                                <Stack direction="row" spacing={1} alignItems="center" sx={{mb: 2, color: '#374151'}}>
                                    <FileText size={18} />
                                    <Typography sx={{fontWeight: 500}}>Itens da Fatura</Typography>
                                </Stack>

                                {!itens.length ? (
                                    <Typography color="text.secondary">Nenhum item retornado para esta fatura.</Typography>
                                ) : (
                                    <Stack spacing={1.5}>
                                        {itens.map((item, index) => (
                                            <Box key={item.id || `${item.descricao}-${index}`} sx={{bgcolor: '#fff', border: '1px solid #e5e7eb', borderRadius: 2, p: 2}}>
                                                <Stack direction={{xs: 'column', sm: 'row'}} justifyContent="space-between" spacing={2} sx={{mb: item.licenca_id || item.plano_id ? 1 : 0}}>
                                                    <Box>
                                                        <Typography sx={{color: '#111827', fontWeight: 500}}>{item.descricao || '-'}</Typography>
                                                        <Typography variant="body2" sx={{color: '#6b7280'}}>{item.tipo || '-'}</Typography>
                                                    </Box>
                                                    <Typography sx={{color: '#111827', fontWeight: 500}}>{formatMoney(item.valor)}</Typography>
                                                </Stack>
                                                {item.licenca_id || item.plano_id ? (
                                                    <Typography variant="body2" sx={{color: '#6b7280'}}>
                                                        Licença: {item.licenca_id || '-'} | Plano: {item.plano_id || '-'}
                                                    </Typography>
                                                ) : null}
                                            </Box>
                                        ))}
                                    </Stack>
                                )}

                                <Box sx={{mt: 2, pt: 2, borderTop: '1px solid #e5e7eb'}}>
                                    <Stack direction="row" justifyContent="space-between" alignItems="center">
                                        <Typography sx={{fontSize: '1.125rem', color: '#374151'}}>Total</Typography>
                                        <Typography sx={{fontSize: '1.5rem', color: '#111827', fontWeight: 600}}>
                                            {formatMoney(fatura.valor_total)}
                                        </Typography>
                                    </Stack>
                                </Box>
                            </Box>

                            ) : null}

                            <Box sx={{bgcolor: '#f9fafb', borderRadius: 2, p: 2}}>
                                <Stack direction="row" spacing={1} alignItems="center" sx={{mb: 2, color: '#374151'}}>
                                    <Receipt size={18} />
                                    <Typography sx={{fontWeight: 500}}>Cobranças</Typography>
                                </Stack>
                                <CobrancasDaFatura key={fatura.id} faturaId={fatura.id} aoMudar={onChanged} />
                            </Box>

                            <Box sx={{display: 'grid', gridTemplateColumns: {xs: '1fr', sm: '1fr 1fr'}, gap: 2}}>
                                <Box>
                                    <Typography variant="body2" sx={{color: '#6b7280'}}>Criado em</Typography>
                                    <Typography sx={{color: '#111827'}}>{formatDateBR(fatura.criado_em)}</Typography>
                                </Box>
                                <Box>
                                    <Typography variant="body2" sx={{color: '#6b7280'}}>Atualizado em</Typography>
                                    <Typography sx={{color: '#111827'}}>{formatDateBR(fatura.atualizado_em)}</Typography>
                                </Box>
                            </Box>
                        </Stack>
                    )}
                </DialogContent>

                <Box sx={{px: 3, pb: 3, pt: 0}}>
                    {pdf.erro ? <Alert severity="error" sx={{mb: 2}} onClose={() => setPdf({carregando: false, erro: ''})}>{pdf.erro}</Alert> : null}
                    <Stack direction={{xs: 'column', md: 'row'}} alignItems={{md: 'center'}} justifyContent="space-between" spacing={2} sx={{borderTop: '1px solid #e5e7eb', pt: 3}}>
                        <Stack direction={{xs: 'column', sm: 'row'}} spacing={1.5}>
                            {canReprocess ? (
                                <Button
                                    variant="outlined"
                                    color="warning"
                                    startIcon={<RefreshCw size={16} />}
                                    onClick={() => setReprocessOpen(true)}
                                    disabled={saving}
                                >
                                    Reprocessar Fatura
                                </Button>
                            ) : null}
                            <Button
                                variant="outlined"
                                startIcon={<Download size={16} />}
                                onClick={abrirPdf}
                                disabled={pdf.carregando || loading || !fatura}
                            >
                                {pdf.carregando ? 'Gerando o PDF...' : 'PDF da fatura'}
                            </Button>
                            <Button
                                variant="outlined"
                                startIcon={<Bell size={16} />}
                                onClick={onNotify}
                                disabled={saving || loading || !fatura}
                            >
                                Reenviar a fatura
                            </Button>
                        </Stack>
                        <Button onClick={onClose} variant="contained" color="inherit" sx={{bgcolor: '#e5e7eb', color: '#374151', '&:hover': {bgcolor: '#d1d5db'}}}>
                            Fechar
                        </Button>
                    </Stack>
                </Box>
            </Dialog>

            <FaturaReprocessDialog
                open={reprocessOpen}
                fatura={{...fatura, itens}}
                loading={saving}
                onClose={() => setReprocessOpen(false)}
                onConfirm={async () => {
                    const success = await onReprocess?.();
                    if (success !== false) {
                        setReprocessOpen(false);
                    }
                }}
            />
        </>
    );
}
